import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useWebSocketContext } from '@shared/context/WebSocketProvider';
import { useProfile } from '@shared/api/useProfile';
import { attachmentApi } from '@entities/attachment';
import { wsBuilder, useChatStore } from '@entities/messanger';

export const useSendMessage = (chatId) => {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { sendJsonMessage } = useWebSocketContext();
  const { data: profile } = useProfile();

  const addOptimisticMessage = useChatStore((state) => state.addOptimisticMessage);
  const failOptimisticMessage = useChatStore((state) => state.failOptimisticMessage);

  // Ядро отправки: оптимистично добавляет сообщение и шлёт команду в WS.
  // Возвращает Promise<boolean> — отправлено или нет.
  const sendMessage = useCallback(
    async (rawText, rawFile = null) => {
      const trimmed = rawText?.trim();
      if (!trimmed && !rawFile) return false;

      // rawFile — либо локальный File (с устройства), либо уже загруженный Attachment
      // (выбран в «Моих файлах») — тогда повторно грузить его не нужно
      const isUploaded = Boolean(rawFile?.id);

      const tempId = `temp-${Date.now()}`;
      const now = Math.floor(Date.now() / 1000);

      // Оптимистично добавляем сообщение в историю
      addOptimisticMessage({
        id: tempId,
        chat_id: chatId,
        sender_id: profile?.id,
        data: trimmed || '',
        attachments: rawFile
          ? [{ name: rawFile.name, size: rawFile.size, file: rawFile.file ?? null }]
          : null,
        timestamp: now,
        is_read: false,
        pending: true,
        failed: false,
      });

      setIsLoading(true);
      try {
        let attachment = null;
        if (rawFile) {
          attachment = isUploaded
            ? rawFile
            : (await attachmentApi.upload(rawFile))?.data?.attachment;
        }
        // Связываем id команды с tempId — если сервер ответит ошибкой
        // или отклонением, пометим сообщение failed
        const command = wsBuilder.message(chatId, trimmed || '', attachment);
        useChatStore.getState().registerCommandTempId(command.id, tempId);
        sendJsonMessage(command);
        return true;
      } catch (e) {
        failOptimisticMessage(tempId);
        toast.error('Не удалось отправить сообщение');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [chatId, sendJsonMessage, profile?.id, addOptimisticMessage, failOptimisticMessage]
  );

  const send = useCallback(async () => {
    const sent = await sendMessage(text, file);
    if (sent) {
      setText('');
      setFile(null);
    }
  }, [sendMessage, text, file]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        send();
      }
    },
    [send]
  );

  const retry = useCallback(
    (tempId, messageText, messageFile) => {
      setText(messageText);
      setFile(messageFile);
      // Удаляем failed сообщение и отправляем заново
      useChatStore.getState().removeOptimisticMessage(tempId);
      // send() сработает при следующем клике/enter
    },
    []
  );

  return {
    text,
    setText,
    file,
    setFile,
    isLoading,
    send,
    sendMessage,
    handleKeyDown,
    retry,
  };
};
